"use client"
import { useUser } from "@/context/UserContext"
import { admin_impersonate } from "@/firebase/clientApp"
import { FormEvent, useCallback, useRef, useState } from "react"
import { signInWithCustomToken } from "@firebase/auth"
import { useFirebase } from "@/firebase/FirebaseProvider"
import { Button } from "@/components/catalyst/button"
import { Dialog } from "@/components/catalyst/dialog"
import { Input } from "@/components/catalyst/input"
import { notifyBugsnag } from "@/support/bugsnag/bugsnags"
import { LoadingSpinnerV2 } from "@/components/LoadingSpinner"
import { Navbar, NavbarItem } from "@/components/catalyst/navbar"
import { Description, Label } from "@/components/catalyst/fieldset"
import { Switch, SwitchField } from "@/components/catalyst/switch"

export default function AuthenticationDot() {
  const { auth, functions, debugOptions, setDebugOptions, resetDebugOptions } =
    useFirebase()
  const { loadingUser, userV2: user, userRole } = useUser()
  const [isLoadingSwitch, setIsLoadingSwitch] = useState(false)
  const [showDebugOptions, setShowDebugOptions] = useState(false)

  const inputRef = useRef<HTMLInputElement>(null)

  const [showDialog, setShowDialog] = useState(false)

  const canImpersonate = userRole === "ADMIN" || userRole === "STAFF"
  const isLoadingAuth = loadingUser || isLoadingSwitch
  const isSignedOut = !isLoadingAuth && !user
  const isSignedIn = !isLoadingAuth && user

  const onSwitchPressed = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()

      const impersonateUserID = inputRef.current?.value
      if (!impersonateUserID) return

      if (isLoadingSwitch) return

      try {
        setIsLoadingSwitch(true)
        const token = await admin_impersonate(functions, {
          userID: impersonateUserID,
        })
        await signInWithCustomToken(auth, token.data.token)
        window.location.reload()
      } catch (err) {
        let errorMsg = "Error performing switch"
        if (typeof err === "string") {
          errorMsg = err
        } else if (err && typeof err === "object" && "message" in err) {
          errorMsg = `${(err as { message: string }).message}`
        }
        alert(errorMsg)
        notifyBugsnag(err)
      } finally {
        setIsLoadingSwitch(false)
      }
    },
    [auth, functions, isLoadingSwitch],
  )

  const appVersion = process.env.NEXT_PUBLIC_BUGSNAG_APP_VERSION ?? "unset"
  let displayVersion = appVersion
  if (process.env.NODE_ENV === "development") {
    displayVersion = `(local dev)`
  } else if (appVersion === "unset") {
    displayVersion = `(version unknown)`
  }

  return (
    <>
      <Dialog
        className={"space-y-2"}
        open={showDialog}
        onClose={() => {
          if (isLoadingSwitch) return
          setShowDialog(false)
        }}
      >
        <Navbar className={"mb-6"}>
          <NavbarItem
            current={!showDebugOptions}
            onClick={() => {
              if (isLoadingSwitch) return
              setShowDebugOptions(false)
            }}
          >
            User Info
          </NavbarItem>
          <NavbarItem
            current={showDebugOptions}
            onClick={() => {
              if (isLoadingSwitch) return
              setShowDebugOptions(true)
            }}
          >
            Debug Options
          </NavbarItem>
          <div className="flex flex-1 justify-end">
            <Button
              plain
              className="cursor-pointer"
              disabled={isLoadingSwitch}
              onClick={() => setShowDialog(false)}
            >
              <span className="text-brand-blue">Close</span>
            </Button>
          </div>
        </Navbar>
        {!showDebugOptions && (
          <>
            <div className="text-lg font-bold text-gray-800">Current User</div>
            {!user && <div className="text-md">No logged in user </div>}
            {user && (
              <div>
                <div className="text-md">
                  <span className="font-bold text-gray-600">Name: </span>
                  <span className="text-gray-800">
                    {user.displayName ?? "Not set"}
                  </span>
                </div>
                <div className="text-md">
                  <span className="font-bold text-gray-600">Email: </span>
                  <span className="text-gray-800">{user.email}</span>
                </div>
                <div className="text-md">
                  <span className="font-bold text-gray-600">Role: </span>
                  <span className="text-gray-800">{userRole}</span>
                </div>
                <div className="text-md">
                  <span className="font-bold text-gray-600">ID: </span>
                  <span className="text-gray-800">{user.uid}</span>
                </div>
              </div>
            )}
            <div className="text-lg font-semibold text-gray-800">
              Impersonate
            </div>
            {canImpersonate && (
              <div className={"space-y-1"}>
                <div className="text-md">
                  <span className="text-gray-800">
                    Provide UID to impersonate:
                  </span>
                </div>
                <form
                  onSubmit={onSwitchPressed}
                  className={"flex flex-row gap-2"}
                >
                  <Input
                    type={"text"}
                    ref={inputRef}
                    disabled={isLoadingSwitch}
                  />
                  <Button type={"submit"} className="cursor-pointer">
                    {!isLoadingSwitch && "Switch"}
                    {isLoadingSwitch && <LoadingSpinnerV2 />}
                  </Button>
                </form>
              </div>
            )}
            {!canImpersonate && <p>Unavailable for this user</p>}
            <div className={"h-3"} />
            <Button
              plain
              className={"w-full"}
              disabled={isLoadingSwitch}
              onClick={() => {
                setShowDialog(false)
                auth.signOut().catch(notifyBugsnag)
              }}
            >
              Log out
            </Button>
            <div className="flex flex-row justify-center text-sm text-neutral-400">
              {displayVersion}
            </div>
          </>
        )}

        {showDebugOptions && (
          <>
            <SwitchField>
              <Label>Fail all backend function calls</Label>
              <Description>Mimic failures in the Newie Backend</Description>
              <Switch
                checked={debugOptions.failFirebaseFunctions}
                onChange={(value) => {
                  setDebugOptions((prev) => ({
                    ...prev,
                    failFirebaseFunctions: value,
                  }))
                }}
              />
            </SwitchField>
            <Button
              plain
              className={"w-full"}
              disabled={isLoadingSwitch}
              onClick={resetDebugOptions}
            >
              Reset to defaults
            </Button>
          </>
        )}
      </Dialog>
      <div className={"flex items-center justify-center"}>
        {isSignedOut && (
          <div
            data-testid={"AUTH_DOT"}
            className={"h-3 w-3 rounded-lg bg-red-500"}
            onClick={() => {
              if (isLoadingAuth) return
              setShowDialog(true)
            }}
          />
        )}
        {isSignedIn && (
          <div
            data-testid={"AUTH_DOT"}
            className={"h-3 w-3 cursor-pointer rounded-lg bg-green-500"}
            onClick={() => {
              if (isLoadingAuth) return
              setShowDialog(true)
            }}
          />
        )}
        {isLoadingAuth && (
          <div
            data-testid={"AUTH_DOT"}
            className={"h-3 w-3 rounded-lg bg-blue-500"}
          />
        )}
      </div>
    </>
  )
}
